import { createSlice } from "@reduxjs/toolkit";
import { set } from "lodash";
import { STATUS, EVENTS, ACTIONS } from "react-joyride";

// ============================================================================
// CONSTANTS
// ============================================================================

/** Timing constants for animations (in ms) */
const TIMING = {
  NAVIGATION: 1500,
  DRAWER: 400,
  DRAWER_CLOSE: 600,
  QUICK: 300,
  DRAWER_CLOSE_BEFORE_TOUR: 500,
};

/** Routes used in tours */
const ROUTES = {
  BALANCE: "/merchant/balance",
  WAREHOUSE: "/merchant/warehouse",
  PRICE_CALCULATOR: "/merchant/mapit-ship/price-calculator",
  SHIPMENTS: "/merchant/mapit-ship/shipments?status=ORDER_PENDING",
  SHIPMENTS_ASSIGNED:
    "/merchant/mapit-ship/shipments?page=1&limit=100&status=ORDER_ASSIGNED_TO_SHIPPING_COMPANY",
};

/** Joyride statuses that indicate tour completion */
const FINISHED_STATUSES = [STATUS.FINISHED, STATUS.SKIPPED];

// Track if we're using the fallback flow (no data rows) for first tour
let firstTourUseFallback = false;

// ============================================================================
// DOM UTILITY FUNCTIONS
// ============================================================================

/**
 * Wait for an element to appear in the DOM
 * @param {string} selector - CSS selector for the element
 * @param {number} timeout - Maximum time to wait in ms
 * @param {number} pollInterval - Interval between checks in ms
 * @returns {Promise<Element|null>} - The element if found, null if timeout
 */
const waitForElement = (selector, timeout = 10000, pollInterval = 100) => {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkElement = () => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        resolve(null);
        return;
      }

      setTimeout(checkElement, pollInterval);
    };

    checkElement();
  });
};

/**
 * Wait for MUI DataGrid to finish loading
 * Checks for loading indicators and waits for them to disappear
 * @param {number} timeout - Maximum time to wait
 * @returns {Promise<boolean>} - True if loading finished, false if timeout
 */
const waitForDataGridLoaded = async (timeout = 15000) => {
  const startTime = Date.now();

  // First, wait a bit for the grid to potentially start loading
  await new Promise((resolve) => setTimeout(resolve, 500));

  return new Promise((resolve) => {
    const checkLoading = () => {
      // Check for various MUI DataGrid loading indicators
      const loadingOverlay = document.querySelector(
        ".MuiDataGrid-overlay--loading",
      );
      const loadingSpinner = document.querySelector(
        ".MuiDataGrid-root .MuiCircularProgress-root",
      );
      const skeletonRows = document.querySelector(
        ".MuiDataGrid-root .MuiSkeleton-root",
      );

      const isLoading = loadingOverlay || loadingSpinner || skeletonRows;

      if (!isLoading) {
        // Additional wait to ensure data is rendered
        setTimeout(() => resolve(true), 300);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        resolve(false);
        return;
      }

      setTimeout(checkLoading, 200);
    };

    checkLoading();
  });
};

/**
 * Scroll an element into view and wait for scroll to complete
 * @param {string} selector - CSS selector for the element
 * @param {object} options - ScrollIntoView options
 * @returns {Promise<Element|null>} - The element if found, null otherwise
 */
const scrollElementIntoView = async (
  selector,
  options = { behavior: "smooth", block: "center" },
) => {
  const element = document.querySelector(selector);
  if (!element) {
    return null;
  }

  element.scrollIntoView(options);

  // Wait for scroll animation to complete
  await new Promise((resolve) => setTimeout(resolve, 500));

  return element;
};

/**
 * Programmatically click an element and wait for a target element to appear
 * @param {string} clickSelector - Selector of element to click
 * @param {string} waitSelector - Selector of element to wait for after click
 * @param {number} timeout - Maximum time to wait
 * @returns {Promise<boolean>} - True if target appeared, false if timeout
 */
const clickAndWaitFor = async (clickSelector, waitSelector, timeout = 5000) => {
  const elementToClick = document.querySelector(clickSelector);
  if (!elementToClick) {
    return false;
  }

  // Scroll element into view first
  elementToClick.scrollIntoView({ behavior: "smooth", block: "center" });
  await new Promise((resolve) => setTimeout(resolve, 300));

  elementToClick.click();

  const targetElement = await waitForElement(waitSelector, timeout);
  return !!targetElement;
};

// ============================================================================
// REDUX SLICE
// ============================================================================

const slice = createSlice({
  name: "tutorial",
  initialState: {
    run: false,
    stepIndex: 0,
    tourId: null,
    tourActive: false,
    openCreditPopper: false,
    openSidebarDialog: false,
    openDrawer: false,
    selectedMapitShipTabIndex: null,
  },
  reducers: {
    setRun: (state, action) => {
      state.run = action.payload;
    },
    setStepIndex: (state, action) => {
      state.stepIndex = action.payload;
    },
    setTourId: (state, action) => {
      state.tourId = action.payload;
    },
    setTourActive: (state, action) => {
      state.tourActive = action.payload;
    },
    setOpenCreditPopper: (state, action) => {
      state.openCreditPopper = action.payload;
    },
    setOpenSidebarDialog: (state, action) => {
      state.openSidebarDialog = action.payload;
    },
    setOpenDrawer: (state, action) => {
      state.openDrawer = action.payload;
    },
    resetTourUI: (state) => {
      state.run = false;
      state.openCreditPopper = false;
      state.openSidebarDialog = false;
      state.openDrawer = false;
      state.selectedMapitShipTabIndex = null;
    },
    setSelectedMapitShipTabIndex: (state, action) => {
      state.selectedMapitShipTabIndex = action.payload;
    },
  },
});

export const {
  setRun,
  setStepIndex,
  setTourId,
  setTourActive,
  setOpenCreditPopper,
  setOpenSidebarDialog,
  setOpenDrawer,
  resetTourUI,
  setSelectedMapitShipTabIndex,
} = slice.actions;

// ============================================================================
// THUNK ACTIONS
// ============================================================================

/**
 * Start a tour with the given ID after a delay
 * @param {string} tourId - The tour identifier
 * @param {number} delay - Delay in ms before starting (default: 2000)
 */
export const startTour =
  (tourId, delay = 2000) =>
  (dispatch) => {
    setTimeout(() => {
      dispatch(setTourId(tourId));
      dispatch(setRun(true));
    }, delay);
  };

/**
 * Start a tour from Help Center drawer
 * Closes the drawer first, then starts the tour
 * @param {string} tourId - The tour identifier
 * @param {Function} closeDrawer - Function to close the help center drawer
 */
export const startTourFromHelpCenter = (tourId, closeDrawer) => (dispatch) => {
  // Close the help center drawer first
  closeDrawer();

  // Wait for drawer animation to complete, then start tour
  setTimeout(() => {
    dispatch(setTourId(tourId));
    dispatch(setStepIndex(0));
    dispatch(setRun(true));
  }, TIMING.DRAWER_CLOSE_BEFORE_TOUR);
};

// ============================================================================
// FIRST TOUR HANDLER
// ============================================================================

/**
 * First Tour Steps Overview:
 * 0:  body (Welcome)
 * 1:  #toobar-credit-details - Credit popper trigger
 * 2:  #popper-credit-details - Credit popper content
 * 3:  #charge-amount-cards - Balance page (/merchant/balance)
 * 4:  #warehouse-sidebar - Warehouse page (/merchant/warehouse)
 * 5:  #warehouse-add-btn - Add warehouse button
 * 6:  #mapitShip-btn - MapitShip sidebar button
 * 7:  #price-calculator-btn - Price calculator in sidebar
 * 8:  #price-calculator-card - Price calculator page
 * 9:  #view-prices-btn - View prices button
 * 10: #mapitShip-btn - MapitShip sidebar button (again)
 * 11: #shipment-btn - Shipment button in sidebar
 * 12: #create-order-btn - Shipments page
 * 13: .order-table - Order table
 * 14: #choses-company-order - Choose company
 * 15: #step-ORDER_ASSIGNED_TO_SHIPPING_COMPANY - Status tab (navigates to /merchant/mapit-ship/shipments?page=1&limit=100&status=ORDER_ASSIGNED_TO_SHIPPING_COMPANY)
 * 16: .MuiDataGrid-row - Data grid row (dynamic - waits for data, skips to 18 if no rows)
 * 17: #mapitShip-download-awb-btn - Download AWB button (programmatically clicks row first)
 * 18: body - No data fallback (shows image when no shipments exist)
 * 19: body (Final/Congratulations)
 */

/**
 * Helper class to manage tour transitions with cleaner API
 */
class TourTransitionManager {
  constructor({ dispatch, navigate, isMobile, nextIndex }) {
    this.dispatch = dispatch;
    this.navigate = navigate;
    this.isMobile = isMobile;
    this.nextIndex = nextIndex;
  }

  /** Go to next/prev step */
  goToStep = () => this.dispatch(setStepIndex(this.nextIndex));

  /** Navigate to a route and resume tour after navigation completes */
  navigateAndResume = (path) => {
    this.dispatch(setRun(false));
    this.navigate(path);
    setTimeout(() => {
      this.dispatch(setStepIndex(this.nextIndex));
      this.dispatch(setRun(true));
    }, TIMING.NAVIGATION);
  };

  /** Execute action with optional delay (mobile-aware) */
  withMobileDelay = (action, delay = TIMING.DRAWER) => {
    if (this.isMobile) {
      setTimeout(action, delay);
    } else {
      action();
    }
  };

  /** Set drawer state */
  setDrawer = (open) => this.dispatch(setOpenDrawer(open));

  /** Set sidebar dialog state */
  setSidebar = (open) => this.dispatch(setOpenSidebarDialog(open));

  /** Set credit popper state */
  setPopper = (open) => this.dispatch(setOpenCreditPopper(open));

  /** Set selected MapitShip tab index */
  setSelectedMapitShipTabIndex = (index) =>
    this.dispatch(setSelectedMapitShipTabIndex(index));

  /** Execute different actions for mobile vs desktop */
  mobileOrDesktop = (mobileAction, desktopAction) => {
    if (this.isMobile) {
      mobileAction();
    } else {
      desktopAction();
    }
  };
}

/**
 * Get transition configuration for the first tour
 * Returns a map of "stepIndex_direction" -> handler function
 */
const getFirstTourTransitions = (manager) => {
  const {
    goToStep,
    navigateAndResume,
    setDrawer,
    setSidebar,
    setPopper,
    mobileOrDesktop,
    withMobileDelay,
    setSelectedMapitShipTabIndex,
  } = manager;

  return {
    // Steps 0-2: Credit Popper Flow
    "0_next": () => {
      setPopper(true);
      goToStep();
    },
    "1_prev": () => {
      setPopper(false);
      goToStep();
    },
    "1_next": goToStep,
    "2_prev": goToStep,
    "2_next": () => {
      setPopper(false);
      navigateAndResume(ROUTES.BALANCE);
    },

    // Step 3: Balance Page
    "3_prev": () => {
      setPopper(true);
      withMobileDelay(goToStep);
    },
    "3_next": () =>
      mobileOrDesktop(
        () => {
          setDrawer(true);
          setTimeout(() => navigateAndResume(ROUTES.WAREHOUSE), TIMING.DRAWER);
        },
        () => navigateAndResume(ROUTES.WAREHOUSE),
      ),

    // Steps 4-5: Warehouse Page
    "4_next": () =>
      mobileOrDesktop(() => {
        setDrawer(false);
        setTimeout(goToStep, TIMING.DRAWER_CLOSE);
      }, goToStep),
    "4_prev": () =>
      mobileOrDesktop(
        () => {
          setDrawer(false);
          setTimeout(() => navigateAndResume(ROUTES.BALANCE), TIMING.QUICK);
        },
        () => navigateAndResume(ROUTES.BALANCE),
      ),
    "5_next": () =>
      mobileOrDesktop(() => {
        setDrawer(true);
        setTimeout(() => {
          setSidebar(true);
          goToStep();
        }, TIMING.DRAWER);
      }, goToStep),
    "5_prev": () =>
      mobileOrDesktop(() => {
        setDrawer(true);
        setTimeout(goToStep, TIMING.DRAWER_CLOSE);
      }, goToStep),

    // Steps 6-7: Sidebar Navigation
    "6_next": () => {
      setSidebar(true);
      goToStep();
    },
    "6_prev": () =>
      mobileOrDesktop(
        () => {
          setDrawer(false);
          setTimeout(() => {
            setSidebar(false);
            goToStep();
          }, TIMING.DRAWER);
        },
        () => {
          setSidebar(false);
          goToStep();
        },
      ),
    "7_next": () =>
      mobileOrDesktop(
        () => {
          setDrawer(false);
          setTimeout(() => {
            setSidebar(false);
            navigateAndResume(ROUTES.PRICE_CALCULATOR);
          }, TIMING.DRAWER);
        },
        () => {
          setSidebar(false);
          navigateAndResume(ROUTES.PRICE_CALCULATOR);
        },
      ),
    "7_prev": () => navigateAndResume(ROUTES.WAREHOUSE),

    // Steps 8-9: Price Calculator Page
    "8_next": goToStep,
    "8_prev": () =>
      mobileOrDesktop(
        () => {
          setDrawer(true);
          setTimeout(() => {
            setSidebar(true);
            goToStep();
          }, TIMING.DRAWER);
        },
        () => {
          setSidebar(true);
          goToStep();
        },
      ),
    "9_next": () =>
      mobileOrDesktop(() => {
        setDrawer(true);
        setTimeout(goToStep, TIMING.DRAWER);
      }, goToStep),
    "9_prev": goToStep,

    // Steps 10-11: Back to Sidebar
    "10_next": () => {
      setSidebar(true);
      goToStep();
    },
    "10_prev": () =>
      mobileOrDesktop(
        () => {
          setDrawer(false);
          setTimeout(
            () => navigateAndResume(ROUTES.PRICE_CALCULATOR),
            TIMING.DRAWER,
          );
        },
        () => navigateAndResume(ROUTES.PRICE_CALCULATOR),
      ),
    "11_next": () =>
      mobileOrDesktop(
        () => {
          setDrawer(false);
          setTimeout(() => {
            setSidebar(false);
            navigateAndResume(ROUTES.SHIPMENTS);
          }, TIMING.DRAWER);
        },
        () => {
          setSidebar(false);
          navigateAndResume(ROUTES.SHIPMENTS);
        },
      ),
    "11_prev": () => {
      setSidebar(false);
      goToStep();
    },

    // Step 12: Shipments Page
    "12_prev": () =>
      mobileOrDesktop(
        () => {
          setDrawer(true);
          setTimeout(() => {
            setSidebar(true);
            goToStep();
          }, TIMING.DRAWER);
        },
        () => {
          setSidebar(true);
          goToStep();
        },
      ),

    // Steps 14-19: Download AWB Flow
    // Step 14: #choses-company-order - Navigate to assigned shipments route
    // "14_next": () => {
    //   setSelectedMapitShipTabIndex(1);
    //   setTimeout(goToStep, TIMING.NAVIGATION);
    // },
    // "14_prev": goToStep,

    // // Step 15: #step-ORDER_ASSIGNED_TO_SHIPPING_COMPANY - Wait for data and check if rows exist
    // "15_prev": () => {
    //   setSelectedMapitShipTabIndex(0);
    //   setTimeout(goToStep, TIMING.NAVIGATION);
    // },
    // "15_next": async () => {
    //   manager.dispatch(setRun(false));

    //   // Wait for DataGrid to load
    //   await waitForDataGridLoaded();

    //   // Check if there are any data rows
    //   const dataRow = document.querySelector('.MuiDataGrid-row');

    //   if (dataRow) {
    //     // Data exists - scroll first row into view before showing spotlight
    //     firstTourUseFallback = false;

    //     // Scroll the first row into view so spotlight appears correctly
    //     await scrollElementIntoView('.MuiDataGrid-row:first-child', { behavior: 'smooth', block: 'center' });

    //     manager.dispatch(setStepIndex(manager.nextIndex));
    //     manager.dispatch(setRun(true));
    //   } else {
    //     // No data - skip to step 18 (no data fallback) to show image explaining AWB download
    //     firstTourUseFallback = true;
    //     manager.dispatch(setStepIndex(18));
    //     manager.dispatch(setRun(true));
    //   }
    // },

    // Step 16: .MuiDataGrid-row - Click row and wait for dialog
    // "16_prev": goToStep,
    // "16_next": async () => {
    //   manager.dispatch(setRun(false));

    //   // Click the first data grid row
    //   const rowClicked = await clickAndWaitFor(
    //     '.MuiDataGrid-row:first-child',
    //     '.MuiDialog-root',
    //     5000
    //   );

    //   if (rowClicked) {
    //     // Dialog opened, now wait for the AWB button to appear inside it
    //     const awbButton = await waitForElement('#mapitShip-download-awb-btn', 8000);

    //     if (awbButton) {
    //       // Give extra time for dialog animations to complete
    //       await new Promise(resolve => setTimeout(resolve, 500));
    //       manager.dispatch(setStepIndex(manager.nextIndex));
    //       manager.dispatch(setRun(true));
    //     } else {
    //       // AWB button not found in dialog, skip to completion
    //       console.log('AWB button not found in dialog, skipping to completion');
    //       manager.dispatch(setStepIndex(19));
    //       manager.dispatch(setRun(true));
    //     }
    //   } else {
    //     // Dialog didn't open, skip to completion
    //     console.log('Dialog did not open, skipping to completion');
    //     manager.dispatch(setStepIndex(19));
    //     manager.dispatch(setRun(true));
    //   }
    // },

    // Step 17: #mapitShip-download-awb-btn - Download AWB button
    // "17_prev": () => {
    //   // Close the dialog if open and go back
    //   const closeBtn = document.querySelector('[aria-label="close"]') ||
    //     document.querySelector('.MuiDialog-root .MuiIconButton-root');
    //   if (closeBtn) {
    //     closeBtn.click();
    //   }
    //   setTimeout(goToStep, 300);
    // },
    // "17_next": () => {
    //   // Close the dialog if open before going to completion
    //   const closeBtn = document.querySelector('[aria-label="close"]') ||
    //     document.querySelector('.MuiDialog-root .MuiIconButton-root');
    //   if (closeBtn) {
    //     closeBtn.click();
    //   }
    //   // Skip fallback step (18) and go directly to completion (19)
    //   manager.dispatch(setStepIndex(19));
    // },

    // // Step 18: body - No data fallback (shows image when no shipments exist)
    // "18_prev": () => {
    //   // Go back to step 15 (status tab) since we skipped steps 16-17 due to no data
    //   manager.dispatch(setStepIndex(15));
    // },
    // "18_next": goToStep, // Go to step 19 (completion)

    // Step 19: body - Completion (no special handling needed, default goToStep works)
  };
};

/**
 * Creates the first tour callback handler for Joyride
 * @param {Array} _steps - The tour steps array (unused, kept for API compatibility)
 * @returns {Function} Curried function: (data, navigate, isMobile) => (dispatch) => void
 */
export const createFirstTourHandler =
  (_steps) => (data, navigate, isMobile) => (dispatch) => {
    const { status, type, index, action } = data;

    // Handle tour completion
    if (FINISHED_STATUSES.includes(status)) {
      dispatch(resetTourUI());
      firstTourUseFallback = false;
      localStorage.setItem("isFirstTourCompleted", "true");
      return;
    }

    // Handle manual close (X button)
    if (action === ACTIONS.CLOSE) {
      dispatch(resetTourUI());
      localStorage.setItem("isFirstTourCompleted", "true");
      return;
    }

    // Handle TARGET_NOT_FOUND for step 17 (Download AWB button in dialog)
    // The dialog might not be fully rendered yet, so we wait and retry
    if (type === EVENTS.TARGET_NOT_FOUND && index === 17) {
      console.log("Step 17 target not found, waiting for dialog...");
      dispatch(setRun(false));

      // Wait for the AWB button to appear
      waitForElement("#mapitShip-download-awb-btn", 8000).then((element) => {
        if (element) {
          console.log("AWB button found, resuming tour");
          setTimeout(() => {
            dispatch(setRun(true));
          }, 300);
        } else {
          console.log(
            "AWB button not found after waiting, going to step 19 (completion)",
          );
          // Skip the fallback (18) and go directly to completion (19)
          dispatch(setStepIndex(19));
          dispatch(setRun(true));
        }
      });
      return;
    }

    // Only process step transitions
    if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) {
      return;
    }

    const isNext = action === ACTIONS.NEXT;
    const isPrev = action === ACTIONS.PREV;
    const nextIndex = index + (isPrev ? -1 : 1);

    // Create transition manager
    const manager = new TourTransitionManager({
      dispatch,
      navigate,
      isMobile,
      nextIndex,
    });

    // Get and execute transition
    const transitions = getFirstTourTransitions(manager);
    const transitionKey = `${index}_${isNext ? "next" : "prev"}`;
    const transition = transitions[transitionKey];

    transition ? transition() : manager.goToStep();
  };

// ============================================================================
// WAREHOUSE TOUR HANDLER
// ============================================================================

/**
 * Warehouse Tour Steps Overview:
 * 0: body (Welcome)
 * 1: #warehouse-sidebar - Navigate to warehouse
 * 2: #warehouse-add-btn - Add warehouse button
 * 3: body (Completion)
 */

const getWarehouseTourTransitions = (manager) => {
  const { goToStep, navigateAndResume, setDrawer, mobileOrDesktop } = manager;

  return {
    // Step 0: Welcome
    "0_next": () =>
      mobileOrDesktop(() => {
        setDrawer(true);
        setTimeout(goToStep, TIMING.DRAWER);
      }, goToStep),

    // Step 1: Warehouse sidebar
    "1_prev": () =>
      mobileOrDesktop(() => {
        setDrawer(false);
        setTimeout(goToStep, TIMING.DRAWER_CLOSE);
      }, goToStep),
    "1_next": () =>
      mobileOrDesktop(
        () => {
          setDrawer(false);
          setTimeout(() => navigateAndResume(ROUTES.WAREHOUSE), TIMING.DRAWER);
        },
        () => navigateAndResume(ROUTES.WAREHOUSE),
      ),

    // Step 2: Add warehouse button
    "2_prev": () =>
      mobileOrDesktop(() => {
        setDrawer(true);
        setTimeout(goToStep, TIMING.DRAWER);
      }, goToStep),
  };
};

export const createWarehouseTourHandler =
  (_steps) => (data, navigate, isMobile) => (dispatch) => {
    const { status, type, index, action } = data;

    if (FINISHED_STATUSES.includes(status)) {
      dispatch(resetTourUI());
      return;
    }

    if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) {
      return;
    }

    const isNext = action === ACTIONS.NEXT;
    const isPrev = action === ACTIONS.PREV;
    const nextIndex = index + (isPrev ? -1 : 1);

    const manager = new TourTransitionManager({
      dispatch,
      navigate,
      isMobile,
      nextIndex,
    });
    const transitions = getWarehouseTourTransitions(manager);
    const transitionKey = `${index}_${isNext ? "next" : "prev"}`;
    const transition = transitions[transitionKey];

    transition ? transition() : manager.goToStep();
  };

// ============================================================================
// SHIPMENT TOUR HANDLER
// ============================================================================

/**
 * Shipment Tour Steps Overview:
 * 0: body (Welcome)
 * 1: #mapitShip-btn - MapitShip sidebar button
 * 2: #shipment-btn - Shipment button in sidebar
 * 3: #create-order-btn - Create shipment button
 * 4: .order-table - Order table
 * 5: #choses-company-order - Choose company
 * 6: body (Completion)
 */

const getShipmentTourTransitions = (manager) => {
  const {
    goToStep,
    navigateAndResume,
    setDrawer,
    setSidebar,
    mobileOrDesktop,
  } = manager;

  return {
    // Step 0: Welcome
    "0_next": () =>
      mobileOrDesktop(() => {
        setDrawer(true);
        setTimeout(goToStep, TIMING.DRAWER);
      }, goToStep),

    // Step 1: MapitShip button
    "1_prev": () =>
      mobileOrDesktop(() => {
        setDrawer(false);
        setTimeout(goToStep, TIMING.DRAWER_CLOSE);
      }, goToStep),
    "1_next": () => {
      setSidebar(true);
      goToStep();
    },

    // Step 2: Shipment button in sidebar
    "2_prev": () => {
      setSidebar(false);
      goToStep();
    },
    "2_next": () =>
      mobileOrDesktop(
        () => {
          setDrawer(false);
          setTimeout(() => {
            setSidebar(false);
            navigateAndResume(ROUTES.SHIPMENTS);
          }, TIMING.DRAWER);
        },
        () => {
          setSidebar(false);
          navigateAndResume(ROUTES.SHIPMENTS);
        },
      ),

    // Step 3: Create order button
    "3_prev": () =>
      mobileOrDesktop(
        () => {
          setDrawer(true);
          setTimeout(() => {
            setSidebar(true);
            goToStep();
          }, TIMING.DRAWER);
        },
        () => {
          setSidebar(true);
          goToStep();
        },
      ),
  };
};

export const createShipmentTourHandler =
  (_steps) => (data, navigate, isMobile) => (dispatch) => {
    const { status, type, index, action } = data;

    if (FINISHED_STATUSES.includes(status)) {
      dispatch(resetTourUI());
      return;
    }

    if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) {
      return;
    }

    const isNext = action === ACTIONS.NEXT;
    const isPrev = action === ACTIONS.PREV;
    const nextIndex = index + (isPrev ? -1 : 1);

    const manager = new TourTransitionManager({
      dispatch,
      navigate,
      isMobile,
      nextIndex,
    });
    const transitions = getShipmentTourTransitions(manager);
    const transitionKey = `${index}_${isNext ? "next" : "prev"}`;
    const transition = transitions[transitionKey];

    transition ? transition() : manager.goToStep();
  };

// ============================================================================
// CHARGE WALLET TOUR HANDLER
// ============================================================================

/**
 * Charge Wallet Tour Steps Overview:
 * 0: body (Welcome)
 * 1: #toobar-credit-details - Credit popper trigger
 * 2: #popper-credit-details - Credit popper content
 * 3: #charge-amount-cards - Balance page
 * 4: body (Completion)
 */

const getChargeWalletTourTransitions = (manager) => {
  const { goToStep, navigateAndResume, setPopper, withMobileDelay } = manager;

  return {
    // Step 0: Welcome
    "0_next": () => {
      setPopper(true);
      goToStep();
    },

    // Step 1: Credit details trigger
    "1_prev": () => {
      setPopper(false);
      goToStep();
    },
    "1_next": goToStep,

    // Step 2: Credit popper content
    "2_prev": goToStep,
    "2_next": () => {
      setPopper(false);
      navigateAndResume(ROUTES.BALANCE);
    },

    // Step 3: Charge amount cards
    "3_prev": () => {
      setPopper(true);
      withMobileDelay(goToStep);
    },
  };
};

export const createChargeWalletTourHandler =
  (_steps) => (data, navigate, isMobile) => (dispatch) => {
    const { status, type, index, action } = data;

    if (FINISHED_STATUSES.includes(status)) {
      dispatch(resetTourUI());
      return;
    }

    if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) {
      return;
    }

    const isNext = action === ACTIONS.NEXT;
    const isPrev = action === ACTIONS.PREV;
    const nextIndex = index + (isPrev ? -1 : 1);

    const manager = new TourTransitionManager({
      dispatch,
      navigate,
      isMobile,
      nextIndex,
    });
    const transitions = getChargeWalletTourTransitions(manager);
    const transitionKey = `${index}_${isNext ? "next" : "prev"}`;
    const transition = transitions[transitionKey];

    transition ? transition() : manager.goToStep();
  };

// ============================================================================
// PRICE CALCULATOR TOUR HANDLER
// ============================================================================

/**
 * Price Calculator Tour Steps Overview:
 * 0: body (Welcome)
 * 1: #mapitShip-btn - MapitShip sidebar button
 * 2: #price-calculator-btn - Price calculator in sidebar
 * 3: #price-calculator-card - Price calculator form
 * 4: #view-prices-btn - View prices button
 * 5: body (Completion)
 */

const getPriceCalculatorTourTransitions = (manager) => {
  const {
    goToStep,
    navigateAndResume,
    setDrawer,
    setSidebar,
    mobileOrDesktop,
  } = manager;

  return {
    // Step 0: Welcome
    "0_next": () =>
      mobileOrDesktop(() => {
        setDrawer(true);
        setTimeout(goToStep, TIMING.DRAWER);
      }, goToStep),

    // Step 1: MapitShip button
    "1_prev": () =>
      mobileOrDesktop(() => {
        setDrawer(false);
        setTimeout(goToStep, TIMING.DRAWER_CLOSE);
      }, goToStep),
    "1_next": () => {
      setSidebar(true);
      goToStep();
    },

    // Step 2: Price calculator button in sidebar
    "2_prev": () => {
      setSidebar(false);
      goToStep();
    },
    "2_next": () =>
      mobileOrDesktop(
        () => {
          setDrawer(false);
          setTimeout(() => {
            setSidebar(false);
            navigateAndResume(ROUTES.PRICE_CALCULATOR);
          }, TIMING.DRAWER);
        },
        () => {
          setSidebar(false);
          navigateAndResume(ROUTES.PRICE_CALCULATOR);
        },
      ),

    // Step 3: Price calculator card
    "3_prev": () =>
      mobileOrDesktop(
        () => {
          setDrawer(true);
          setTimeout(() => {
            setSidebar(true);
            goToStep();
          }, TIMING.DRAWER);
        },
        () => {
          setSidebar(true);
          goToStep();
        },
      ),
  };
};

export const createPriceCalculatorTourHandler =
  (_steps) => (data, navigate, isMobile) => (dispatch) => {
    const { status, type, index, action } = data;

    if (FINISHED_STATUSES.includes(status)) {
      dispatch(resetTourUI());
      return;
    }

    if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) {
      return;
    }

    const isNext = action === ACTIONS.NEXT;
    const isPrev = action === ACTIONS.PREV;
    const nextIndex = index + (isPrev ? -1 : 1);

    const manager = new TourTransitionManager({
      dispatch,
      navigate,
      isMobile,
      nextIndex,
    });
    const transitions = getPriceCalculatorTourTransitions(manager);
    const transitionKey = `${index}_${isNext ? "next" : "prev"}`;
    const transition = transitions[transitionKey];

    transition ? transition() : manager.goToStep();
  };

// ============================================================================
// DOWNLOAD AWB TOUR HANDLER
// ============================================================================

/**
 * Download AWB Tour Steps Overview:
 * 0: body (Welcome)
 * 1: #mapitShip-btn - MapitShip sidebar button
 * 2: #shipment-btn - Shipment button in sidebar
 * 3: #step-ORDER_ASSIGNED_TO_SHIPPING_COMPANY - Status tab
 * 4: .MuiDataGrid-row - Data grid row (dynamic)
 * 5: #mapitShip-download-awb-btn - Download AWB button
 * 6: body (No Data Fallback - shows when no shipments exist)
 * 7: body (Completion)
 */

// Track if we're using the fallback flow (no data rows)
let downloadAwbUseFallback = false;

const getDownloadAwbTourTransitions = (manager) => {
  const {
    dispatch,
    goToStep,
    navigateAndResume,
    setDrawer,
    setSidebar,
    mobileOrDesktop,
    nextIndex,
  } = manager;

  return {
    // Step 0: Welcome
    "0_next": () =>
      mobileOrDesktop(() => {
        setDrawer(true);
        setTimeout(goToStep, TIMING.DRAWER);
      }, goToStep),

    // Step 1: MapitShip button
    "1_prev": () =>
      mobileOrDesktop(() => {
        setDrawer(false);
        setTimeout(goToStep, TIMING.DRAWER_CLOSE);
      }, goToStep),
    "1_next": () => {
      setSidebar(true);
      goToStep();
    },

    // Step 2: Shipment button in sidebar
    "2_prev": () => {
      setSidebar(false);
      goToStep();
    },
    "2_next": () =>
      mobileOrDesktop(
        () => {
          setDrawer(false);
          setTimeout(() => {
            setSidebar(false);
            navigateAndResume(ROUTES.SHIPMENTS_ASSIGNED);
          }, TIMING.DRAWER);
        },
        () => {
          setSidebar(false);
          navigateAndResume(ROUTES.SHIPMENTS_ASSIGNED);
        },
      ),

    // Step 3: Status tab - Navigate to assigned shipments and wait for data
    "3_prev": () =>
      mobileOrDesktop(
        () => {
          setDrawer(true);
          setTimeout(() => {
            setSidebar(true);
            goToStep();
          }, TIMING.DRAWER);
        },
        () => {
          setSidebar(true);
          goToStep();
        },
      ),
    // "3_next": async () => {
    //   console.log("3_next...");
    //   dispatch(setRun(false));

    //   // Wait for DataGrid to load
    //   await waitForDataGridLoaded();

    //   // Check if there are any data rows
    //   const dataRow = document.querySelector('.MuiDataGrid-row');

    //   if (dataRow) {
    //     // Data exists - proceed normally to step 4 (data grid row)
    //     downloadAwbUseFallback = false;
    //     dispatch(setStepIndex(nextIndex));
    //     dispatch(setRun(true));
    //   } else {
    //     // No data - go to step 6 (no data fallback) to show image explaining AWB download
    //     downloadAwbUseFallback = true;
    //     dispatch(setStepIndex(6)); // Go to NO_DATA_FALLBACK step
    //     dispatch(setRun(true));
    //   }
    // },

    // Step 4: Data grid row - Click row and wait for dialog
    // "4_prev": goToStep,
    // "4_next": async () => {
    //   dispatch(setRun(false));

    //   // Click the first data grid row
    //   const success = await clickAndWaitFor(
    //     '.MuiDataGrid-row:first-child',
    //     '#mapitShip-download-awb-btn',
    //     5000
    //   );

    //   if (success) {
    //     dispatch(setStepIndex(nextIndex));
    //     dispatch(setRun(true));
    //   } else {
    //     // If dialog didn't open, try to continue anyway
    //     dispatch(setStepIndex(nextIndex));
    //     dispatch(setRun(true));
    //   }
    // },

    // Step 5: Download AWB button
    // "5_prev": () => {
    //   // Close the dialog if open and go back
    //   const closeBtn = document.querySelector('[aria-label="close"]') ||
    //     document.querySelector('.MuiDialog-root .MuiIconButton-root');
    //   if (closeBtn) {
    //     closeBtn.click();
    //   }
    //   setTimeout(goToStep, 300);
    // },
  };
};

export const createDownloadAwbTourHandler =
  (_steps) => (data, navigate, isMobile) => (dispatch) => {
    const { status, type, index, action } = data;

    if (FINISHED_STATUSES.includes(status)) {
      dispatch(resetTourUI());
      downloadAwbUseFallback = false; // Reset fallback flag
      return;
    }

    if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) {
      return;
    }

    const isNext = action === ACTIONS.NEXT;
    const isPrev = action === ACTIONS.PREV;
    const nextIndex = index + (isPrev ? -1 : 1);

    const manager = new TourTransitionManager({
      dispatch,
      navigate,
      isMobile,
      nextIndex,
    });
    // Expose dispatch to manager for async operations
    manager.dispatch = dispatch;

    const transitions = getDownloadAwbTourTransitions(manager);
    const transitionKey = `${index}_${isNext ? "next" : "prev"}`;
    const transition = transitions[transitionKey];

    transition ? transition() : manager.goToStep();
  };

export default slice.reducer;
